import { baseApi, defaultQueryFreshness } from "./baseApi";
import type { BountyItem, CreateBountyPayload } from "../../api/bounties";
import type { PagedResult } from "../../api/trades";

export const bountiesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getBounties: builder.query<PagedResult<BountyItem>, Record<string, string | number | boolean | undefined> | void>({
      query: (params) => ({ url: "/bounties", method: "GET", params, usePublic: true }),
      providesTags: (result) => [
        { type: "Bounties", id: "LIST" },
        ...((result?.items || []).map((item) => ({ type: "Bounties" as const, id: String(item.id) }))),
      ],
      ...defaultQueryFreshness,
    }),
    getBountyById: builder.query<BountyItem, string>({
      query: (id) => ({ url: `/bounties/${id}`, method: "GET", usePublic: true }),
      providesTags: (_res, _err, id) => [{ type: "Bounties", id: String(id) }],
      ...defaultQueryFreshness,
    }),
    createBounty: builder.mutation<unknown, CreateBountyPayload>({
      query: (payload) => {
        const formData = new FormData();
        formData.append("userId", payload.userId);
        formData.append("cardName", payload.cardName);
        formData.append("game", payload.game);
        if (payload.cardGame) formData.append("cardGame", payload.cardGame);
        if (payload.cardId) formData.append("cardId", payload.cardId);
        if (payload.cardNameCanonical) formData.append("cardNameCanonical", payload.cardNameCanonical);
        if (payload.zipCode) formData.append("zipCode", payload.zipCode);
        if (payload.desiredCondition) formData.append("desiredCondition", payload.desiredCondition);
        if (payload.gradingCompany) formData.append("gradingCompany", payload.gradingCompany);
        if (payload.gradeNumber) formData.append("gradeNumber", payload.gradeNumber);
        if (payload.radius !== undefined) formData.append("radius", String(payload.radius));
        formData.append("file", payload.file, payload.file instanceof File ? payload.file.name : "bounty.webp");
        return { url: "/bounties", method: "POST", data: formData, timeout: 30000 };
      },
      invalidatesTags: [{ type: "Bounties", id: "LIST" }],
    }),
    createBountyMatch: builder.mutation<unknown, { bountyId: string; payload: Record<string, unknown> }>({
      query: ({ bountyId, payload }) => ({ url: `/bounty-matches/${bountyId}`, method: "POST", data: payload }),
      invalidatesTags: (_res, _err, { bountyId }) => [{ type: "Bounties", id: String(bountyId) }],
    }),
    getBountyMatchCount: builder.query<{ matchCount?: number }, string>({
      query: (bountyId) => ({ url: `/bounty-matches/${bountyId}`, method: "GET" }),
      providesTags: (_res, _err, bountyId) => [{ type: "Bounties", id: `matches:${bountyId}` }],
      ...defaultQueryFreshness,
    }),
    getMyBountyMatches: builder.query<unknown[], string>({
      query: (userId) => ({ url: "/my-bounty-matches", method: "GET", params: { userId } }),
      providesTags: (_res, _err, userId) => [{ type: "Bounties", id: `myMatches:${userId}` }],
      ...defaultQueryFreshness,
    }),
    deleteBounty: builder.mutation<unknown, string>({
      query: (id) => ({ url: `/bounties/${id}`, method: "DELETE" }),
      invalidatesTags: (_res, _err, id) => [{ type: "Bounties", id: String(id) }, { type: "Bounties", id: "LIST" }],
    }),
    updateBountyLocation: builder.mutation<unknown, { id: string; location: Record<string, unknown> }>({
      query: ({ id, location }) => ({ url: `/bounties/${id}/location`, method: "PATCH", data: location }),
      invalidatesTags: (_res, _err, { id }) => [{ type: "Bounties", id: String(id) }, { type: "Bounties", id: "LIST" }],
    }),
  }),
});

export const {
  useGetBountiesQuery,
  useGetBountyByIdQuery,
  useCreateBountyMutation,
  useCreateBountyMatchMutation,
  useGetBountyMatchCountQuery,
  useGetMyBountyMatchesQuery,
  useDeleteBountyMutation,
  useUpdateBountyLocationMutation,
} = bountiesApi;
